import { useLanguage } from "../../context/LanguageContext";
import { DashboardData } from "../../lib/languages";
import { Card, CardContent } from "../../components/ui/card";
import {
  BookCheck,
  BookMarkedIcon,
  Crown,
  GraduationCap,
  UserSquare2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { BarCharLabel } from "../../components/Charts/BarCharLabel";
import { RadarChartMulti } from "../../components/Charts/RadarChart";
import { Link } from "react-router-dom";
import { cn, DateFormate, parseField } from "../../lib/utils";
import StarRating from "../../components/StarRating";
import { useEffect, useMemo, useState } from "react";
import { Avatar } from "antd";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../lib/api";
import { DonutWithText } from "../../components/Charts/DonutWithText";
import axios from "axios";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";

export default function AdminOverview() {
  const { language } = useLanguage();
  const t = DashboardData[language] || DashboardData["en"];

  const [loadingData, setLoadingData] = useState(true);
  const [data, setData] = useState({
    student_number: 0,
    teacher_number: 0,
    rate_site: [],
    course_number: 0,
    lesson_number: 0,
    lessons_day: [],
    lessons_coming: [],
    lessons_attended: [],
    subscription: {},
    teacherRate: [],
    country_student: {},
    subscription_course: {},
    all_users: 0,
  });

  const getAllData = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/admin/dashboard`);
      setData(data?.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    getAllData();
  }, []);

  const pluralize = (count, singular, plural) =>
    `${count} ${count === 1 ? singular : plural}`;

  // Chart helpers
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const parsedData = Object.entries(data.subscription || {})
    .map(([ym, subs]) => {
      const [year, month] = ym.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1);
      const label = monthNames[date.getMonth()];
      return {
        month: label,
        subscriptions: subs.length,
        revenue: subs.reduce((sum, s) => sum + s.amount, 0),
      };
    })
    .sort((a, b) => monthNames.indexOf(a.month) - monthNames.indexOf(b.month));

  const subscriptionData = parsedData.map((d) => ({
    month: d.month,
    desktop: d.subscriptions,
  }));
  const revenueData = parsedData.map((d) => ({
    month: d.month,
    desktop: d.revenue,
  }));

  // Teacher rates
  const sorted = [...data.teacherRate]
    .map((t) => ({ ...t, numericRate: Number(t.rate) || 0 }))
    .sort((a, b) => b.numericRate - a.numericRate)
    .slice(0, 4);
  const [showRates, setShowRates] = useState(3);

  // Lessons arrays
  const lessons_day = data.lessons_day || [];
  const lessons_coming = data.lessons_coming || [];
  const lessons_attended = data.lessons_attended || [];

  // View state for lessons
  const [view, setView] = useState(null);
  useEffect(() => {
    setView(
      lessons_day.length
        ? "lessons_day"
        : lessons_coming.length
        ? "lessons_coming"
        : "lessons_attended"
    );
  }, [lessons_day, lessons_coming, lessons_attended]);

  // Helper: extract string from localized object
  const getText = (obj, fallback = "") =>
    obj && typeof obj === "object"
      ? obj[language] ?? obj.en ?? Object.values(obj)[0] ?? fallback
      : obj ?? fallback;

  const categories = [
    {
      value: "lessons_day",
      label: `Today's (${lessons_day.length})`,
    },
    {
      value: "lessons_coming",
      label: `Upcoming (${lessons_coming.length})`,
    },
    {
      value: "lessons_attended",
      label: `Attended (${lessons_attended.length})`,
    },
  ];

  const dataMap = { lessons_day, lessons_coming, lessons_attended };
  const selected = dataMap[view] || [];

  // Compute booking duration
  const computeDuration = (start, end) => {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let dh = eh - sh;
    let dm = em - sm;
    if (dm < 0) {
      dh -= 1;
      dm += 60;
    }
    return `${dh}h ${dm}m`;
  };

  // Student chart
  const chartData = useMemo(
    () =>
      Object.entries(data.country_student).map(([country, students], idx) => ({
        name: country,
        value: students.length,
        fill: `hsl(var(--chart-${(idx % 5) + 1}))`,
      })),
    [data.country_student]
  );

  const chartConfig = useMemo(() => {
    const base = { value: { label: "Students" } };
    Object.keys(data.country_student).forEach((country, idx) => {
      base[country] = {
        label: country,
        color: `hsl(var(--chart-${(idx % 5) + 1}))`,
      };
    });
    return base;
  }, [data.country_student]);

  const totalStudents = chartData.reduce((sum, slice) => sum + slice.value, 0);

  if (loadingData) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="w-full h-32" />
          <Skeleton className="w-full h-32" />
          <Skeleton className="w-full h-32" />
          <Skeleton className="w-full h-32" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="w-full h-56" />
          <Skeleton className="w-full h-56" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="w-full h-32" />
          <Skeleton className="w-full h-32" />
          <Skeleton className="w-full h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DetailsOverviewCard
          Icon={UserSquare2}
          title="Number of teachers"
          description={pluralize(data.teacher_number, "teacher", "teachers")}
          to="/admin/users"
        />
        <DetailsOverviewCard
          Icon={BookMarkedIcon}
          title="Number of courses available"
          description={pluralize(data.course_number, "course", "courses")}
        />
        <DetailsOverviewCard
          Icon={BookCheck}
          title="Number of lessons"
          description={pluralize(data.lesson_number, "lesson", "lessons")}
        />
        <DetailsOverviewCard
          Icon={GraduationCap}
          title="Number of students"
          description={pluralize(data.student_number, "student", "students")}
          to="/admin/users"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {subscriptionData.length > 0 ? (
          <BarCharLabel
            color="#2563eb"
            title="Number of subscriptions to the platform"
            footerTitle="Number of monthly subscriptions"
            data={subscriptionData}
            label="Subscriptions"
          />
        ) : (
          <div className="text-center text-gray-500 w-full py-8">
            {t.noSubscriptions || "No subscription data available."}
          </div>
        )}

        {revenueData.length > 0 ? (
          <BarCharLabel
            color="#f87171"
            title="Revenue"
            footerTitle="Monthly revenue"
            data={revenueData}
            label="Revenue"
          />
        ) : (
          <div className="text-center text-gray-500 w-full py-8">
            {t.noRevenue || "No revenue data available."}
          </div>
        )}
      </div>

      {/* Lessons */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-bold text-xl">Lessons</p>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-fit border-none focus:ring-0 p-0 shadow-none">
              <span className="text-sm text-muted-foreground">
                <SelectValue placeholder="Select type" />
              </span>
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {selected.length > 0 ? (
            selected.map((lesson) => {
              const booking = lesson.booking || {};
              const time =
                booking.start_time && booking.end_time
                  ? computeDuration(booking.start_time, booking.end_time)
                  : "—";
              const titleText = getText(
                parseField(lesson.title, language),
                "Untitled"
              );
              const descText = getText(
                parseField(lesson.description, language),
                ""
              );

              return (
                <ActivityCard
                  key={lesson.id}
                  title={titleText}
                  description={descText}
                  time={time}
                  to={`/admin/lesson-view/${lesson.id}`}
                />
              );
            })
          ) : (
            <div className="col-span-full text-center text-gray-500">
              {t.noLessons || "No lessons to display."}
            </div>
          )}
        </div>
      </div>

      {/* Teacher Rate */}
      <div className="space-y-4">
        <p className="font-bold text-xl">Teachers rating</p>
        <Card className="border-2 border-blue-600 overflow-auto">
          <CardContent className="p-4 space-y-4">
            <table className="w-full text-base">
              <tbody>
                {sorted.length > 0 ? (
                  sorted.map((tObj, idx) => (
                    <tr key={tObj.id}>
                      <td className="p-2 font-bold flex items-center gap-4 whitespace-nowrap md:whitespace-normal">
                        <div className="relative inline-block">
                          <span>{idx + 1}</span>
                          {idx === 0 && (
                            <div className="absolute -top-4 -left-1">
                              <Crown className="size-5 text-yellow-400 fill-yellow-400" />
                            </div>
                          )}
                        </div>
                        <span>{tObj.user.name}</span>
                      </td>
                      <td className="p-2 font-medium whitespace-nowrap">
                        {tObj.name_job}
                      </td>
                      <td className="p-2 font-medium whitespace-nowrap">
                        {tObj.reviews.length} comments
                      </td>
                      <td className="p-2 font-medium whitespace-nowrap flex items-center gap-2">
                        {tObj.numericRate} /5{" "}
                        <StarRating rating={tObj.numericRate} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-500">
                      {t.noTeacherRatings || "No teacher ratings yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Radar Chart & Site Rates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {chartData.length > 0 ? (
          <DonutWithText
            chartData={chartData}
            chartConfig={chartConfig}
            titleText="Students by Country"
            descriptionText={`Total: ${totalStudents.toLocaleString()}`}
            centerLabel="Students"
          />
        ) : (
          <div className="text-center text-gray-500 w-full py-8">
            {t.noStudentData || "No student data available."}
          </div>
        )}

        <Card>
          <CardContent className="space-y-4 p-4">
            <div className="flex items-center justify-between">
              <p className="font-bold text-xl">Site Rates</p>
              <Button
                variant="link"
                className="text-muted-foreground p-0 font-normal"
                onClick={() => setShowRates((prev) => prev + 3)}
                disabled={showRates >= data.rate_site.length}
              >
                Show More
              </Button>
            </div>

            <div className="space-y-4 h-fit overflow-auto">
              {data.rate_site && data.rate_site.length > 0 ? (
                data.rate_site
                  .slice(0, showRates)
                  .map(({ id, reviewer, message, created_at, rate }) => (
                    <StudentComments
                      key={id}
                      avatar={reviewer?.image}
                      name={reviewer?.name}
                      comment={message}
                      timestamp={DateFormate(created_at)}
                      rating={rate ? Number(rate) : undefined}
                    />
                  ))
              ) : (
                <div className="py-8 text-center text-gray-500">
                  {t.noSiteRates || "No site rates yet."}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const DetailsOverviewCard = ({ Icon, title, description, Percentage, to }) => {
  return (
    <Link to={to}>
      <Card className="border-2 border-blue-600 ">
        <CardContent className="p-4 text-center flex flex-col justify-center items-center gap-2">
          <Icon className="text-blue-600 size-8" />
          <p className="text-muted-foreground text-xs font-medium">{title}</p>
          <div className="flex items-center gap-2">
            <p className="font-bold text-2xl lg:text-xl">{description}</p>
            {Percentage && (
              <div className="border-2 border-green-500 text-green-500 py-1 px-2 rounded-full text-xs font-semibold h-fit mt-1">
                {Percentage}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

function ActivityCard({ title, description, time, to }) {
  return (
    <Link to={to}>
      <Card className="w-full">
        <CardContent className="p-4 space-y-2 flex flex-col justify-between h-full">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-lg truncate flex-1">{title}</p>
            <span className="text-xs font-medium text-gray-500">{time}</span>
          </div>
          {description && (
            <div
              className="text-sm text-muted-foreground line-clamp-2"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

const StudentComments = ({ avatar, name, comment, timestamp, rating }) => {
  return (
    <div className="flex items-center gap-2">
      <Avatar
        className={cn(
          "min-w-12 min-h-12 rounded-full flex items-center justify-center text-xs font-medium",
          "bg-blue-100 text-blue-600",
          "dark:bg-blue-900 dark:text-blue-300"
        )}
        src={`${UrlImage}${avatar}`}
      >
        {name.charAt(0)}
      </Avatar>
      <div className="flex flex-col gap-1 w-full">
        <p className="text-xs font-bold">{name}</p>
        <div className="bg-secondary w-full py-3 px-6 rounded-full">
          <p className="font-medium text-base md:text-lg">{comment}</p>
        </div>
        <p className="text-xs font-medium text-muted-foreground">{timestamp}</p>
      </div>
    </div>
  );
};
